import React, { useState, useEffect } from 'react';
import { 
  Wallet, 
  Send, 
  Download, 
  History, 
  TrendingUp, 
  Gift, 
  Lock, 
  Eye, 
  EyeOff, 
  Copy,
  RefreshCw,
  Plus,
  Minus,
  ArrowUpRight,
  ArrowDownLeft,
  BarChart3,
  Activity,
  Shield
} from 'lucide-react';

interface WalletData {
  address: string;
  vttn_balance: number;
  usdt_balance: number;
  bonus_balance: number;
  locked_balance: number;
  total_balance: number;
  tier: string;
  referral_code: string;
}

interface Transaction {
  id: string;
  hash: string;
  type: string;
  amount: string;
  token: string;
  timestamp: string;
  status: string;
  from?: string;
  to?: string;
}

const WalletPage: React.FC = () => {
  const [walletData, setWalletData] = useState<WalletData | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showBalance, setShowBalance] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [sendAmount, setSendAmount] = useState('');
  const [sendAddress, setSendAddress] = useState('');
  const [selectedToken, setSelectedToken] = useState('VTTN');

  useEffect(() => {
    loadWalletData();
    loadTransactions();
    
    // 재단 지갑에서 선택된 지갑 정보 확인
    const selectedAddress = localStorage.getItem('selectedWalletAddress');
    const selectedName = localStorage.getItem('selectedWalletName');
    const selectedType = localStorage.getItem('selectedWalletType');
    
    if (selectedAddress && selectedName) {
      // 선택된 지갑 정보로 업데이트
      const selectedWalletData: WalletData = {
        address: selectedAddress,
        vttn_balance: 0,
        usdt_balance: 0,
        bonus_balance: 0,
        locked_balance: 0,
        total_balance: 0,
        tier: selectedType || 'Foundation',
        referral_code: 'ADMIN001'
      };
      
      setWalletData(selectedWalletData);
      setIsLoading(false);
      
      // localStorage 정리
      localStorage.removeItem('selectedWalletAddress');
      localStorage.removeItem('selectedWalletName');
      localStorage.removeItem('selectedWalletType');
    }
  }, []);

  const loadWalletData = async () => {
    try {
      // 먼저 localStorage에서 지갑 주소 확인
      const savedAddress = localStorage.getItem('walletAddress');
      const savedWallet = localStorage.getItem('voltronWallet');
      
      let address = null;
      if (savedAddress) {
        address = savedAddress;
      } else if (savedWallet) {
        const wallet = JSON.parse(savedWallet);
        address = wallet.address;
      }
      
      if (address) {
        try {
          const response = await fetch(`http://localhost:3600/api/voltron-wallet/balance/${address}`);
        const data = await response.json();
        
        if (data.success) {
          setWalletData(data.data);
            setIsLoading(false);
            return;
          }
        } catch (apiError) {
          console.log('API 호출 실패, Mock 데이터 사용:', apiError);
        }
        
        // Mock 데이터
        const mockWalletData: WalletData = {
          address: address,
          vttn_balance: 1234.567,
          usdt_balance: 890.123,
          bonus_balance: 45.67,
          locked_balance: 123.45,
          total_balance: 2293.857,
          tier: 'Gold',
          referral_code: 'VOLTRON123'
        };
        
        setWalletData(mockWalletData);
      }
    } catch (error) {
      console.error('지갑 데이터 로딩 실패:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadTransactions = async () => {
    try {
      const savedAddress = localStorage.getItem('walletAddress');
      const savedWallet = localStorage.getItem('voltronWallet');
      
      let address = null;
      if (savedAddress) {
        address = savedAddress;
      } else if (savedWallet) {
        const wallet = JSON.parse(savedWallet);
        address = wallet.address;
      }
      
      if (address) {
        try {
          const response = await fetch(`http://localhost:3600/api/voltron-wallet/transactions/${address}`);
        const data = await response.json();
        
        if (data.success) {
          setTransactions(data.data);
            return;
          }
        } catch (apiError) {
          console.log('API 호출 실패, Mock 데이터 사용:', apiError);
        }
        
        // Mock 트랜잭션 데이터
        const mockTransactions = [
          {
            id: '1',
            type: 'receive',
            amount: '100.5',
            token: 'VTTN',
            from: '0x1234...5678',
            to: address,
            timestamp: new Date(Date.now() - 3600000).toISOString(),
            status: 'completed',
            hash: '0xabc123...def456'
          },
          {
            id: '2',
            type: 'send',
            amount: '50.0',
            token: 'USDT',
            from: address,
            to: '0x9876...5432',
            timestamp: new Date(Date.now() - 7200000).toISOString(),
            status: 'completed',
            hash: '0xdef456...abc123'
          }
        ];
        
        setTransactions(mockTransactions);
      }
    } catch (error) {
      console.error('트랜잭션 로딩 실패:', error);
    }
  };

  const handleSendToken = async () => {
    if (!sendAmount || !sendAddress || !walletData) return;
    
    try {
      const response = await fetch('http://localhost:3600/api/voltron-wallet/transfer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          from_address: walletData.address,
          to_address: sendAddress,
          amount: parseFloat(sendAmount),
          token: selectedToken
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert('전송이 완료되었습니다!');
        setSendAmount('');
        setSendAddress('');
        loadWalletData();
        loadTransactions();
      } else {
        alert('전송 실패: ' + data.error);
      }
    } catch (error) {
      console.error('전송 실패:', error);
      alert('전송 중 오류가 발생했습니다.');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('클립보드에 복사되었습니다!');
  };

  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  const connectWallet = async () => {
    try {
      if (typeof window.ethereum !== 'undefined') {
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
        if (accounts.length > 0) {
          const address = accounts[0];
          localStorage.setItem('walletAddress', address);
          
          // 지갑 상태 변경 이벤트 발생
          window.dispatchEvent(new Event('walletStateChanged'));
          
          // 데이터 다시 로드
          loadWalletData();
          loadTransactions();
        }
      } else {
        alert('MetaMask가 설치되지 않았습니다. MetaMask를 설치해주세요.');
      }
    } catch (error) {
      console.error('지갑 연결 실패:', error);
      alert('지갑 연결에 실패했습니다.');
    }
  };

  if (!walletData) {
    return (
      <div className="space-y-6">
      <div className="text-center py-12">
        <Wallet className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-600 mb-2">지갑이 연결되지 않았습니다</h2>
          <p className="text-gray-500 mb-6">Voltron 네트워크에 연결하여 지갑 기능을 사용하세요.</p>
          <button
            onClick={connectWallet}
            className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium"
          >
            지갑 연결하기
          </button>
        </div>
        
        {/* 지갑 연결 안내 */}
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">지갑 연결 안내</h3>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-orange-600 text-sm font-bold">1</span>
              </div>
              <div>
                <h4 className="font-medium text-gray-900">MetaMask 설치</h4>
                <p className="text-gray-600 text-sm">브라우저에 MetaMask 확장 프로그램을 설치하세요.</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-orange-600 text-sm font-bold">2</span>
              </div>
              <div>
                <h4 className="font-medium text-gray-900">네트워크 추가</h4>
                <p className="text-gray-600 text-sm">Voltron Mainnet (Chain ID: 88889)을 MetaMask에 추가하세요.</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-orange-600 text-sm font-bold">3</span>
              </div>
              <div>
                <h4 className="font-medium text-gray-900">지갑 연결</h4>
                <p className="text-gray-600 text-sm">위의 '지갑 연결하기' 버튼을 클릭하여 지갑을 연결하세요.</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* 블록체인 정보 링크 */}
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Voltron 네트워크 탐색</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => window.open('/blocks', '_blank')}
              className="flex items-center space-x-3 p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
            >
              <BarChart3 className="w-6 h-6 text-blue-600" />
              <div className="text-left">
                <h4 className="font-medium text-blue-900">블록 탐색기</h4>
                <p className="text-blue-600 text-sm">최신 블록 정보</p>
              </div>
            </button>
            <button
              onClick={() => window.open('/transactions', '_blank')}
              className="flex items-center space-x-3 p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
            >
              <Activity className="w-6 h-6 text-green-600" />
              <div className="text-left">
                <h4 className="font-medium text-green-900">트랜잭션</h4>
                <p className="text-green-600 text-sm">거래 내역 조회</p>
              </div>
            </button>
            <button
              onClick={() => window.open('/validators', '_blank')}
              className="flex items-center space-x-3 p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
            >
              <Shield className="w-6 h-6 text-purple-600" />
              <div className="text-left">
                <h4 className="font-medium text-purple-900">검증자</h4>
                <p className="text-purple-600 text-sm">네트워크 검증자</p>
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">My Wallet</h1>
        <button
          onClick={loadWalletData}
          className="flex items-center space-x-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Refresh</span>
        </button>
      </div>

      {/* 지갑 정보 카드 */}
      <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <Wallet className="w-8 h-8" />
            <div>
              <h2 className="text-xl font-bold">
                {localStorage.getItem('selectedWalletName') ? 
                  localStorage.getItem('selectedWalletName') : 'Voltron Wallet'
                }
              </h2>
              <p className="text-orange-100">Tier: {walletData.tier}</p>
              {localStorage.getItem('selectedWalletType') && (
                <p className="text-orange-200 text-sm">
                  재단 지갑 ({localStorage.getItem('selectedWalletType')})
                </p>
              )}
            </div>
          </div>
          <button
            onClick={() => setShowBalance(!showBalance)}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            {showBalance ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
          </button>
        </div>
        
        <div className="flex items-center justify-between mb-4">
          <span className="text-orange-100">Address:</span>
          <div className="flex items-center space-x-2">
            <span className="font-mono">{formatAddress(walletData.address)}</span>
            <button
              onClick={() => copyToClipboard(walletData.address)}
              className="p-1 hover:bg-white/20 rounded"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-orange-100 text-sm">Total Balance</p>
            <p className="text-2xl font-bold">
              {showBalance ? `${walletData.total_balance.toFixed(2)} VTTN` : '••••••'}
            </p>
          </div>
          <div>
            <p className="text-orange-100 text-sm">Referral Code</p>
            <div className="flex items-center space-x-2">
              <p className="text-xl font-bold">{walletData.referral_code}</p>
              <button
                onClick={() => copyToClipboard(walletData.referral_code)}
                className="p-1 hover:bg-white/20 rounded"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 잔액 상세 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Wallet className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">VTTN Balance</p>
              <p className="text-xl font-bold text-gray-900">
                {showBalance ? walletData.vttn_balance.toFixed(2) : '••••••'}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">USDT Balance</p>
              <p className="text-xl font-bold text-gray-900">
                {showBalance ? walletData.usdt_balance.toFixed(2) : '••••••'}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Gift className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Bonus Balance</p>
              <p className="text-xl font-bold text-gray-900">
                {showBalance ? walletData.bonus_balance.toFixed(2) : '••••••'}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Lock className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Locked Balance</p>
              <p className="text-xl font-bold text-gray-900">
                {showBalance ? walletData.locked_balance.toFixed(2) : '••••••'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 탭 메뉴 */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'overview', name: 'Overview', icon: Wallet },
              { id: 'send', name: 'Send', icon: Send },
              { id: 'receive', name: 'Receive', icon: Download },
              { id: 'history', name: 'History', icon: History }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.name}</span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Recent Activity</h3>
              <div className="space-y-3">
                {transactions.slice(0, 5).map((tx) => (
                  <div key={tx.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        tx.type === 'send' ? 'bg-red-100' : 'bg-green-100'
                      }`}>
                        {tx.type === 'send' ? 
                          <ArrowUpRight className="w-4 h-4 text-red-600" /> : 
                          <ArrowDownLeft className="w-4 h-4 text-green-600" />
                        }
                      </div>
                      <div>
                        <p className="font-medium">{tx.type === 'send' ? 'Sent' : 'Received'}</p>
                        <p className="text-sm text-gray-600">{formatDate(tx.timestamp)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-medium ${
                        tx.type === 'send' ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {tx.type === 'send' ? '-' : '+'}{tx.amount} {tx.token}
                      </p>
                      <p className="text-sm text-gray-600">{tx.status}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'send' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Send Tokens</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Token</label>
                  <select
                    value={selectedToken}
                    onChange={(e) => setSelectedToken(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="VTTN">VTTN</option>
                    <option value="USDT">USDT</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Recipient Address</label>
                  <input
                    type="text"
                    value={sendAddress}
                    onChange={(e) => setSendAddress(e.target.value)}
                    placeholder="0x..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
                  <input
                    type="number"
                    value={sendAmount}
                    onChange={(e) => setSendAmount(e.target.value)}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
                <button
                  onClick={handleSendToken}
                  disabled={!sendAmount || !sendAddress}
                  className="w-full bg-orange-500 text-white py-2 px-4 rounded-lg hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  Send {selectedToken}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'receive' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Receive Tokens</h3>
              <div className="text-center space-y-4">
                <div className="bg-gray-100 p-6 rounded-lg">
                  <p className="text-sm text-gray-600 mb-2">Your Wallet Address</p>
                  <div className="flex items-center justify-center space-x-2">
                    <code className="bg-white px-3 py-2 rounded border text-sm">
                      {walletData.address}
                    </code>
                    <button
                      onClick={() => copyToClipboard(walletData.address)}
                      className="p-2 bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <p className="text-sm text-gray-600">
                  Share this address to receive VTTN, USDT, or other tokens on the Voltron Mainnet.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Transaction History</h3>
              <div className="space-y-3">
                {transactions.map((tx) => (
                  <div key={tx.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        tx.type === 'send' ? 'bg-red-100' : 'bg-green-100'
                      }`}>
                        {tx.type === 'send' ? 
                          <ArrowUpRight className="w-5 h-5 text-red-600" /> : 
                          <ArrowDownLeft className="w-5 h-5 text-green-600" />
                        }
                      </div>
                      <div>
                        <p className="font-medium">{tx.type === 'send' ? 'Sent' : 'Received'}</p>
                        <p className="text-sm text-gray-600">{formatDate(tx.timestamp)}</p>
                        <p className="text-xs text-gray-500 font-mono">{tx.hash}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-medium text-lg ${
                        tx.type === 'send' ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {tx.type === 'send' ? '-' : '+'}{tx.amount} {tx.token}
                      </p>
                      <p className="text-sm text-gray-600">{tx.status}</p>
                    </div>
                  </div>
                ))}
                {transactions.length === 0 && (
                  <div className="text-center py-8">
                    <History className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No transactions yet</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WalletPage;